/**
 * types/sync/SyncExport.ts
 * Type definitions for sync export/import file formats
 *
 * Defines the structure for baseline and delta exports using a git-like
 * commit approach with human-readable JSON files and manifest tracking.
 */

import { Brand } from '@enfomentis/shared/config';
import type { SchemaVersionError } from './SyncProvider';
import type { SyncOperation } from './SyncOperation';

/**
 * Baseline export - complete snapshot of all user data
 *
 * Contains all records from all user data tables at a specific point in time.
 * Created on first sync or during baseline compaction.
 *
 * @example
 * {
 *   "type": "baseline",
 *   "exportedAt": "2025-10-01T12:00:00Z",
 *   "userId": "user123",
 *   "deviceId": "laptop-main",
 *   "schemaVersion": 1,
 *   "data": {
 *     "sessions": [...],
 *     "trades": [...],
 *     "accounts": [...]
 *   }
 * }
 */
export interface BaselineExport {
  /** Export type identifier */
  type: 'baseline';

  /** ISO timestamp when this baseline was created */
  exportedAt: string;

  /** User identifier (for future multi-user support) */
  userId: string;

  /** Device identifier that created this baseline */
  deviceId: string;

  /** Schema version for compatibility checking */
  schemaVersion: number;

  /**
   * Last applied sequence per remote device for operation-based sync state restoration.
   * Required for v2 baselines; optional only for v1 backward compatibility.
   * Import should default to empty object {} if missing.
   */
  lastAppliedSeqByDevice?: Record<string, number>;

  /** Complete user data snapshot organized by table */
  data: {
    // Core entities
    accounts?: unknown[];
    account_risks?: unknown[];
    balance_histories?: unknown[];
    sessions?: unknown[];
    trades?: unknown[];
    trade_executions?: unknown[];
    trade_execution_events?: unknown[];
    trade_opportunities?: unknown[];
    strategies?: unknown[];
    playbooks?: unknown[];
    trading_objectives?: unknown[];
    economic_releases?: unknown[];
    market_states?: unknown[];
    notes?: unknown[];

    // Settings entities
    basic_settings?: unknown[];
    pre_trading_settings?: unknown[];
    live_trading_settings?: unknown[];
    post_trading_settings?: unknown[];
    opportunity_settings?: unknown[];
    trading_objective_settings?: unknown[];
    market_snapshot_settings?: unknown[];
    economic_calendar_settings?: unknown[];
    trading_setup_settings?: unknown[];
    sync_settings?: unknown[];

    // Reference/lookup entities
    instruments?: unknown[];
    brokers?: unknown[];
    market_condition_categories?: unknown[];
    playbook_criteria_items?: unknown[];
    confluences?: unknown[];
    entry_model_categories?: unknown[];
    entry_models?: unknown[];
    fee_verification_records?: unknown[];

    // Asset/media entities (metadata only; blobs are device-local)
    stored_images?: unknown[];

    // Transient/draft entities
    session_drafts?: unknown[];
    wizard_drafts?: unknown[];
    note_drafts?: unknown[];
    retired_draft_lineages?: unknown[];
  };
}

/**
 * Delta export - incremental operations since last sync
 *
 * Contains operations representing field-level changes since the specified timestamp.
 * Multiple deltas can be applied on top of a baseline to reconstruct current state.
 *
 * @example
 * {
 *   "type": "delta",
 *   "exportedAt": "2025-10-08T10:30:15Z",
 *   "userId": "user123",
 *   "deviceId": "laptop-main",
 *   "sinceTimestamp": "2025-10-01T12:00:00Z",
 *   "schemaVersion": 1,
 *   "operations": [{ type: "set", table: "trades", recordId: "trade-1", ... }],
 *   "opSeqRanges": { "device-abc": { minSeq: 1, maxSeq: 5 } }
 * }
 */
export interface DeltaExport {
  /** Export type identifier */
  type: 'delta';

  /** ISO timestamp when this delta was created */
  exportedAt: string;

  /** User identifier (for future multi-user support) */
  userId: string;

  /** Device identifier that created this delta */
  deviceId: string;

  /** Timestamp of last sync - changes are since this point */
  sinceTimestamp: string;

  /** Schema version for compatibility checking */
  schemaVersion: number;

  /**
   * Operation-based sync primitives for conflict-free synchronization.
   * Required for all v2 deltas.
   */
  operations: SyncOperation[];

  /**
   * Sequence range metadata for gap detection during import.
   * Reports min/max seq of pending ops per deviceId (exporting device only).
   * Empty object `{}` when no pending operations.
   * Required for all v2 deltas.
   */
  opSeqRanges: {
    [deviceId: string]: {
      minSeq: number;
      maxSeq: number;
    };
  };

  /**
   * Sequences that were compacted away (dropped during coalescing), per device.
   * Used by gap detection to distinguish compaction gaps from true missing operations.
   *
   * Format options:
   * - Individual sequences: `[5, 6, 7]`
   * - Range-compressed (inclusive): `[[5, 10], [15, 20]]`
   *
   * Optional only for v1 artifact compatibility; v2 deltas include when compaction occurs.
   */
  compactedSeqs?: {
    [deviceId: string]: number[] | [number, number][];
  };

}

/**
 * Sync manifest - tracks all baseline and delta files
 *
 * Provides an index of all sync files with metadata for efficient
 * reconstruction of current state and baseline compaction.
 *
 * @example
 * {
 *   "currentBaseline": "baseline_2025-10-01_full.json",
 *   "deltas": [
 *     "delta_2025-10-08_10-30-15.json",
 *     "delta_2025-10-09_14-22-03.json"
 *   ],
 *   "lastExportedAt": "2025-10-09T14:22:03Z"
 * }
 */
export interface SyncManifest {
  /** Filename of the current baseline */
  currentBaseline: string;

  /** Array of delta filenames in chronological order */
  deltas: string[];

  /** ISO timestamp of most recent export (baseline or delta) */
  lastExportedAt: string;

  /** Optional: Total count of all deltas created (for analytics) */
  totalDeltaCount?: number;

  /** Optional: ISO timestamp when current baseline was created */
  baselineCreatedAt?: string;
}

/**
 * Union type for all export formats
 */
export type SyncExportData = BaselineExport | DeltaExport;

/**
 * Type guard to check if export is a baseline
 */
export function isBaselineExport(data: SyncExportData): data is BaselineExport {
  return data.type === 'baseline';
}

/**
 * Type guard to check if export is a delta
 */
export function isDeltaExport(data: SyncExportData): data is DeltaExport {
  return data.type === 'delta';
}

/**
 * Generate baseline filename with timestamp
 *
 * @param timestamp - ISO timestamp for the baseline
 * @returns Filename in format: baseline_YYYY-MM-DD_full.json
 */
export function generateBaselineFilename(timestamp: string): string {
  const date = new Date(timestamp).toISOString().split('T')[0];
  return `baseline_${date}_full.json`;
}

/**
 * Generate delta filename with timestamp
 *
 * @param timestamp - ISO timestamp for the delta
 * @returns Filename in format: delta_YYYY-MM-DD_HH-MM-SS.json
 */
export function generateDeltaFilename(timestamp: string): string {
  const datetime = new Date(timestamp)
    .toISOString()
    .replace(/:/g, '-')
    .replace(/\..+/, '')
    .replace('T', '_');
  return `delta_${datetime}.json`;
}

/**
 * Manifest filename constant
 */
export const SYNC_MANIFEST_FILENAME = 'sync_manifest.json';

/**
 * Sync folder name constant
 */
export const SYNC_FOLDER_NAME = `${Brand.driveFolder}-sync`;

/**
 * Detailed sync operation result
 *
 * Returned by SyncEngine.sync() to provide feedback about what was synced
 */
export interface SyncResult {
  /** Whether sync completed successfully */
  success: boolean;

  /** Type of export that was created */
  type: 'baseline' | 'delta';

  /** Filename of the exported file */
  filename: string;

  /** ISO timestamp when data was exported (empty string if no export occurred) */
  dataExportedAt: string;

  /**
   * ISO timestamp when data was imported from remote (empty string if no import occurred)
   * Only set during bidirectional sync when import actually applied remote data.
   */
  dataImportedAt?: string;

  /**
   * Whether import actually occurred during this sync operation.
   * True when remote data was processed and applied (even if LWW resulted in no changes).
   * Use this flag to determine if lastImportedAt should be updated in Supabase.
   */
  importOccurred?: boolean;

  /** Number of records exported (baseline: total, delta: changes only) */
  recordCount: number;

  /** Number of tables included in export */
  tableCount: number;

  /** Error message if sync failed */
  error?: string;

  /**
   * Schema version validation error details (if import failed due to version mismatch)
   *
   * When set, indicates import was blocked due to schema version issues.
   * UI should check this for specific error handling (toast messages, refresh prompts).
   * The `error` field will also be set with a human-readable message for backward compatibility.
   */
  schemaVersionError?: SchemaVersionError;
}
